import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  Grid,
  Typography,
  Card,
  CardActions,
  CardMedia,
  CardContent,
  Chip,
  Fab,
  Icon
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { imgBaseUrl } from 'configs';
import { BackdropCircular } from 'components';
import { updateQuoteProListingApi } from 'api';
import {
  uploadFileTypeUnsupported,
  uploadFileSizeLimit,
  uploadFileNameUnsupportedSymbols
} from 'redux/actions/general';
import {fileToBase64} from '../../../../utils';
import ProductImageEditor from '../../../catalog/productImageEditor';


const useStyles = makeStyles({
  media: {
    height: '200px'
  },
  disabled: {
    filter: 'brightness(0.6) opacity(0.7)',
    textShadow: '1px 1px #000'
  }
});

const imageRoles = {
  image: 'Base',
  small_image: 'Small',
  thumbnail: 'Thumbnail'
};

const QuoteProductImages = () => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const { editableQuoteProduct, quoteProductLoading, savingProductLoading } = useSelector(
    (state: IReduxState) => state.quote
  );

  const { id = '', sku = '', media_gallery_entries = [] } = editableQuoteProduct || {};

  const { profile: { stores } } = useSelector((state: IReduxState) => state.user);
  const [{ apiUrl }] = stores;
  const mediaStoreUrl = `${apiUrl}/media/catalog/product` || imgBaseUrl;

  const [mediaData, setMediaData] = useState({});
  const [openImageEditor, toggleOpenImageEditor] = useState(false);

  const handleDeleteMedia = (mediaId) => {
    const productToUpdate = {
      id,
      sku,
      media_gallery_entries: editableQuoteProduct.media_gallery_entries.filter((media) => media.id !== mediaId)
    };
    updateQuoteProListingApi({dispatch, product: productToUpdate});
  };

  const handleSaveImage = async (event) => {
    const {files, value} = event.target;

    const fileTypeDotIndexPosition = value.lastIndexOf('.') + 1;
    const slicedFileTypeFromFilePath = value.slice(fileTypeDotIndexPosition);
    const acceptableFormats = 'png|jpeg';
    const hasImageFile = new RegExp(`(image\\/(${acceptableFormats}))`, 'gi');

    if (!hasImageFile.test(event.target.files[0]?.type) && !hasImageFile.test(slicedFileTypeFromFilePath)) {
      dispatch(uploadFileTypeUnsupported());
    } else if (files[0].size / 1024 / 1024 > 1) {
      dispatch(uploadFileSizeLimit('1Mb'));
    } else if (files[0].name.match(/[\^\\/?*:"';<>()|{}\\\\]/g)) {
      dispatch(uploadFileNameUnsupportedSymbols(files[0].name));
    } else {
      const file = await fileToBase64(files[0]);
      const base64Image = btoa(file);
      const [name] = files[0].name.split('.');

      const productToUpdate = {
        id,
        sku,
        media_gallery_entries: [
          ...media_gallery_entries,
          {
            label: name,
            disabled: false,
            media_type: 'image',
            position: media_gallery_entries.length + 1,
            types: !media_gallery_entries.length ? ['image', 'small_image', 'thumbnail'] : [],
            content: {
              base64_encoded_data: base64Image,
              type: files[0].type,
              name
            }
          }
        ]
      };
      updateQuoteProListingApi({dispatch, product: productToUpdate});
    }
  };

  return (
    <div className="w-full">
      <div className="m-4 w-full">
        {!media_gallery_entries.length ?
          <Card elevation={10} className="flex-column h-full items-center justify-center p-10">
            <div>
              <Icon className="pr-8 text-48" fontSize="large" color="primary">cloud_upload</Icon>
            </div>
            <span className="font-bold text-16 text-brand">Upload New Image</span>
            <span className="text-13 text-muted">Files supported: JPG and PNG</span>
            <p className="text-13 text-brand my-2">or</p>
            <label htmlFor="upload-image">
              <Fab
                component="span"
                variant="extended"
                className="border-radius-30 capitalize bg-white text-13 h-30px text-primary font-bold border-1-primary box-shadow-none"
              >
                Browse file
              </Fab>
            </label>
            <input
              className="hidden"
              onChange={handleSaveImage}
              id="upload-image"
              type="file"
              accept="image/png, image/jpeg"
            />
          </Card>
          :
          <>
            <Grid container spacing={3}>
              {media_gallery_entries.map((media, idx) => {
                const {id, file, label, types, disabled} = media;
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={`image-${idx}`}>
                    <Card elevation={10} className="h-full">
                      <CardMedia
                        image={`${mediaStoreUrl}${file}`}
                        title={label}
                        className={`${classes.media} ${disabled && classes.disabled} flex`}
                      >
                        {disabled &&
                          <div className={'m-auto text-36 uppercase text-white'}>
                            Hidden
                          </div>
                        }
                      </CardMedia>
                      <CardContent>
                        <Typography gutterBottom variant="h5" component="h2">
                          {label}
                        </Typography>
                        <div className="min-h-30 mt-2">
                          {!!types.length && types.map((type) => (
                            <Chip
                              key={`label-type-${type}`}
                              label={imageRoles[type]}
                              color="primary"
                              className="mr-2 mb-2"
                            />
                          ))
                          }
                        </div>
                      </CardContent>
                      <CardActions>
                        <Button
                          size="small"
                          variant="outlined"
                          fullWidth
                          onClick={() => handleDeleteMedia(id)}
                        >
                          Delete
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          fullWidth
                          onClick={() => {
                            setMediaData(media);
                            toggleOpenImageEditor(true);
                          }}
                        >
                          Edit
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Card elevation={10} className="flex-column h-full items-center justify-center p-10 text-center">
                  <div>
                    <Icon className="pr-8 text-48" fontSize="large" color="primary">cloud_upload</Icon>
                  </div>
                  <div className="font-bold text-16 text-brand">Add New Image</div>
                  <div className="text-13 text-muted">Files supported: JPEG and PNG</div>
                  <div className="text-13 text-muted mb-4">File size should be less than 1Mb</div>
                  <label htmlFor="upload-image">
                    <Fab
                      component="span"
                      variant="extended"
                      className="border-radius-30 capitalize bg-white text-13 h-30px text-primary font-bold border-1-primary box-shadow-none"
                    >
                      Browse file
                    </Fab>
                  </label>
                  <input
                    className="hidden"
                    onChange={handleSaveImage}
                    id="upload-image"
                    type="file"
                    accept="image/png, image/jpeg"
                  />
                </Card>
              </Grid>
            </Grid>
          </>
        }
        {mediaData &&
          <ProductImageEditor
            product={editableQuoteProduct}
            media={mediaData}
            open={openImageEditor}
            isQuote
            setOpen={(close) => {
              toggleOpenImageEditor(close);
              setMediaData({});
            }}
          />
        }
        <BackdropCircular loading={quoteProductLoading || savingProductLoading} />
      </div>
    </div>
  );
};

export default QuoteProductImages;
